import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip, Collapse } from "@mui/material";
import { BrandMark } from "../branding/Logo";
import {
  LayoutDashboard,
  LineChart,
  Bot,
  FlaskConical,
  Building2,
  KeyRound,
  Coins,
  Waypoints,
  ShieldAlert,
  Bell,
  Settings,
  Users,
  PanelLeftClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronUp,
  Brain,
  Sparkles,
  Activity,
  Shield,
  Wrench,
  ArrowLeftRight,
  CreditCard,
} from "lucide-react";
import { useAppTheme } from "../../design/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";

const iconMap = {
  dashboard: LayoutDashboard,
  "live-charting": LineChart,
  bots: Bot,
  "ai-bot": Brain,
  "arbitrage-bot": ArrowLeftRight,
  backtesting: FlaskConical,
  exchanges: Building2,
  "secure-connections": KeyRound,
  "exchange-symbols": Coins,
  strategies: Waypoints,
  ai: Sparkles,
  administration: Shield,
  "ai-settings": Brain,
  risk: ShieldAlert,
  notifications: Bell,
  settings: Settings,
  users: Users,
  "system-health": Activity,
  "system-settings": Wrench,
  subscription: CreditCard,
};

/* ── Grouped sidebar sections ─────────────────── */
const SIDEBAR_SECTIONS = [
  {
    label: "Main",
    keys: ["dashboard"],
  },
  {
    label: "Trading",
    keys: ["live-charting", "bots", "ai-bot", "arbitrage-bot", "backtesting", "strategies"],
  },
  {
    label: "Configuration",
    keys: ["secure-connections", "ai", "risk", "notifications", "subscription", "settings"],
  },
  {
    label: "Admin",
    keys: ["administration"],
    adminOnly: true,
  },
];

const NavItem = React.memo(function NavItem({ route, isActive, onClick, collapsed }) {
  const Icon = iconMap[route.key] || LayoutDashboard;
  const hasCollapse = route.collapse && route.collapse.length > 0 && !route.noCollapse;
  const [open, setOpen] = useState(isActive && hasCollapse);
  const isActiveLeaf = isActive && !hasCollapse;

  const handleClick = () => {
    if (hasCollapse) {
      setOpen(!open);
    } else {
      onClick(route.route);
    }
  };

  return (
    <div>
      <Tooltip title={collapsed ? route.name : ""} placement="right" arrow>
        <button
          className={`group flex items-center gap-3 w-full rounded-xl ${isActiveLeaf ? "" : "hover-nav"}`}
          onClick={handleClick}
          style={{
            position: "relative",
            padding: collapsed ? "10px" : "11px 14px",
            justifyContent: collapsed ? "center" : "flex-start",
            background: isActiveLeaf ? "var(--gradient-subtle)" : "transparent",
            border: isActiveLeaf ? "1px solid var(--border-accent)" : "1px solid transparent",
            color: isActiveLeaf ? "var(--accent-primary)" : "var(--text-secondary)",
            cursor: "pointer",
            fontSize: "0.8125rem",
            fontWeight: isActiveLeaf ? 600 : 500,
            transition: "all 0.2s ease",
          }}
        >
          {/* Active left accent bar */}
          {isActiveLeaf && !collapsed && (
            <div
              style={{
                position: "absolute",
                left: 0,
                top: "20%",
                bottom: "20%",
                width: 3,
                borderRadius: 2,
                background: "var(--gradient)",
              }}
            />
          )}
          <Icon size={18} style={{ flexShrink: 0 }} />
          {!collapsed && (
            <>
              <span className="flex-1 text-left capitalize truncate">{route.name}</span>
              {hasCollapse && (
                <span
                  style={{
                    marginLeft: "auto",
                    opacity: 0.5,
                    transition: "transform 0.2s ease",
                    transform: open ? "rotate(180deg)" : "rotate(0)",
                  }}
                >
                  <ChevronDown size={14} />
                </span>
              )}
            </>
          )}
        </button>
      </Tooltip>
      {hasCollapse && !collapsed && (
        <Collapse in={open}>
          <div
            className="ml-5 mt-1 space-y-0.5 border-l"
            style={{ borderColor: "var(--border-primary)", paddingLeft: 12 }}
          >
            {route.collapse.map((child) => {
              const childActive = window.location.pathname === child.route;
              return (
                <button
                  key={child.key}
                  className={`flex items-center gap-2 w-full rounded-lg ${childActive ? "" : "hover-nav"}`}
                  onClick={() => onClick(child.route)}
                  style={{
                    padding: "7px 10px",
                    fontSize: "0.8rem",
                    fontWeight: childActive ? 600 : 400,
                    color: childActive ? "var(--accent-primary)" : "var(--text-muted)",
                    background: childActive ? "rgba(99, 102, 241, 0.08)" : "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                    style={{
                      background: childActive ? "var(--accent-primary)" : "var(--text-muted)",
                    }}
                  />
                  <span className="capitalize truncate">{child.name}</span>
                </button>
              );
            })}
          </div>
        </Collapse>
      )}
    </div>
  );
});

export default function Sidebar({ routes = [] }) {
  const { sidebarCollapsed, sidebarMobile, toggleSidebar, closeMobileSidebar } = useAppTheme();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = user?.is_admin;

  const handleNavigate = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      closeMobileSidebar();
    }
  };

  const isActive = (route) => {
    if (route.route === location.pathname) return true;
    if (route.collapse) {
      return route.collapse.some((c) => c.route === location.pathname);
    }
    return false;
  };

  const isVisible = !sidebarCollapsed || sidebarMobile;

  // Build a route lookup map
  const routeMap = {};
  routes
    .filter((r) => r.type === "collapse" && !r.hidden)
    .forEach((r) => {
      routeMap[r.key] = r;
    });

  return (
    <>
      {/* Overlay for mobile */}
      {sidebarMobile && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ backgroundColor: "var(--bg-overlay)", backdropFilter: "blur(4px)" }}
          onClick={closeMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className="fixed top-0 left-0 z-50 h-screen flex flex-col"
        style={{
          width:
            sidebarCollapsed && !sidebarMobile
              ? "var(--sidebar-collapsed)"
              : "var(--sidebar-width)",
          transform: isVisible ? "translateX(0)" : "translateX(-100%)",
          transition:
            "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          backgroundColor: "var(--bg-sidebar)",
          borderRight: "1px solid var(--border-primary)",
        }}
      >
        {/* Logo Header */}
        <div
          className="flex items-center justify-between flex-shrink-0 border-b"
          style={{
            height: "var(--navbar-height)",
            padding: sidebarCollapsed && !sidebarMobile ? "0 12px" : "0 20px",
            borderColor: "var(--border-primary)",
          }}
        >
          <div className="flex items-center gap-2.5">
            <BrandMark size={36} />
            {(!sidebarCollapsed || sidebarMobile) && (
              <div className="flex flex-col min-w-0">
                <span
                  className="font-display font-bold text-sm tracking-tight"
                  style={{ color: "var(--text-primary)" }}
                >
                  TraderMind
                </span>
                <span
                  className="text-[10px] font-semibold tracking-widest uppercase"
                  style={{ color: "var(--accent-primary)" }}
                >
                  AI Trading
                </span>
              </div>
            )}
          </div>

          {(!sidebarCollapsed || sidebarMobile) && (
            <Tooltip title="Collapse sidebar" arrow>
              <button
                onClick={toggleSidebar}
                className="flex items-center justify-center rounded-lg hover-btn"
                style={{
                  width: 28,
                  height: 28,
                  color: "var(--text-muted)",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <PanelLeftClose size={18} />
              </button>
            </Tooltip>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto no-scrollbar py-4 px-3">
          {SIDEBAR_SECTIONS.map((section) => {
            if (section.adminOnly && !isAdmin) return null;

            const sectionRoutes = section.keys.map((key) => routeMap[key]).filter(Boolean);

            if (sectionRoutes.length === 0) return null;

            return (
              <div key={section.label} className="mb-5">
                {(!sidebarCollapsed || sidebarMobile) && (
                  <div
                    className="px-3 mb-2.5"
                    style={{
                      fontSize: "0.625rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      color: "var(--text-muted)",
                    }}
                  >
                    {section.label}
                  </div>
                )}
                {sidebarCollapsed && !sidebarMobile && (
                  <div
                    className="w-6 h-px mx-auto mb-2"
                    style={{ background: "var(--border-primary)" }}
                  />
                )}
                <div className="space-y-1">
                  {sectionRoutes.map((route) => (
                    <NavItem
                      key={route.key}
                      route={route}
                      isActive={isActive(route)}
                      onClick={handleNavigate}
                      collapsed={sidebarCollapsed && !sidebarMobile}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Collapse toggle (when collapsed) */}
      </aside>
    </>
  );
}
